/**
 * Vendor price books — TRBUD-466 Part 3.
 *
 * The Goodyear national account price book, loaded on the vendor page and kept
 * as REFERENCE: what the vendor says it charges, put beside what we paid. Every
 * edition is kept; a wrong one is withdrawn, never edited.
 *
 * The routes (`apps/api/src/routes/price-books.ts`), all `manageVendors`
 * (admin and manager):
 *
 *   POST /vendors/:id/price-books                       the .xlsx upload
 *   POST /vendors/:id/price-books/:bookId/replace       a corrected book, same date, with a reason
 *   POST /vendors/:id/price-books/:bookId/withdraw      with a reason
 *   GET  /vendors/:id/price-books                       the list, live and withdrawn
 *   GET  /vendors/:id/price-books/:bookId/lines?q=      a bounded line search
 *   GET  /vendors/:id/price-book-comparison             what we paid beside the book in force
 *
 * Browser-safe: zod, types, labels and the sentence the panel prints. Money is
 * a decimal string, in USD.
 */
import { z } from "zod";
import { formatMoney } from "./money.js";
import { idempotencyKey } from "./requests.js";

/** The book's four price sheets, in the order the panel lists them. */
export const PRICE_BOOK_SHEETS = ["commercial", "retread", "consumer", "service"] as const;
export type PriceBookSheetKey = (typeof PRICE_BOOK_SHEETS)[number];
export const PRICE_BOOK_SHEET_LABEL: Readonly<Record<PriceBookSheetKey, string>> = {
  commercial: "Commercial",
  retread: "Retread",
  consumer: "Consumer",
  service: "Service pricing",
};

/** The one file type a price book arrives as. */
export const PRICE_BOOK_XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * The per-call file limit, far under the document routes' 64 MB. The real book
 * is about 425 KB; a file ten times that is not a price book.
 */
export const PRICE_BOOK_MAX_BYTES = 5 * 1024 * 1024;

/** A withdrawal's or a replacement's reason, as `price_book.withdrawn_reason` allows. */
export const PRICE_BOOK_REASON_MAX = 2000;
/** The line search box. */
export const PRICE_BOOK_SEARCH_MAX = 100;

export const priceBookReason = z
  .string()
  .trim()
  .min(1, "Say why.")
  .max(PRICE_BOOK_REASON_MAX, `Keep the reason under ${PRICE_BOOK_REASON_MAX} characters.`);

/** `POST /vendors/:id/price-books/:bookId/withdraw`. */
export const withdrawPriceBookBody = z
  .object({
    reason: priceBookReason,
    idempotencyKey,
  })
  .strict();
export type WithdrawPriceBookBody = z.infer<typeof withdrawPriceBookBody>;

/**
 * The multipart fields of `POST /vendors/:id/price-books/:bookId/replace`,
 * sent BEFORE the file part (the server reads only the fields that precede it).
 */
export const replacePriceBookFields = z.object({ reason: priceBookReason }).strict();

/** `GET /vendors/:id/price-books/:bookId/lines`. `q` matches a code, a size or the text. */
export const priceBookLinesQuery = z
  .object({
    q: z.string().trim().max(PRICE_BOOK_SEARCH_MAX).optional(),
    sheet: z.enum(PRICE_BOOK_SHEETS).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(100_000).default(0),
  })
  .strict();
export type PriceBookLinesQuery = z.infer<typeof priceBookLinesQuery>;

/** `GET /vendors/:id/price-book-comparison`. Newest cost day first. */
export const priceBookComparisonQuery = z
  .object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
  })
  .strict();
export type PriceBookComparisonQuery = z.infer<typeof priceBookComparisonQuery>;

/* -------------------------------------------------------------------------- */
/* Books                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * What the account check found (`price_book.account_check`). A clear conflict
 * is refused and never stored.
 *   matched               a number in the vendor's account reference is the
 *                         book's account, common owner or nonsig number;
 *   no_account_reference  the vendor has none, so nothing could be checked;
 *   not_comparable        it has one, but with no number in it.
 */
export const PRICE_BOOK_ACCOUNT_CHECKS = ["matched", "no_account_reference", "not_comparable"] as const;
export type PriceBookAccountCheck = (typeof PRICE_BOOK_ACCOUNT_CHECKS)[number];
export const PRICE_BOOK_ACCOUNT_CHECK_LABEL: Readonly<Record<PriceBookAccountCheck, string>> = {
  matched: "Account matched",
  no_account_reference: "Not checked: no account reference",
  not_comparable: "Not checked: no number in the account reference",
};

export interface PriceBookPerson {
  userId: string;
  name: string;
}

export interface PriceBookSummary {
  priceBookId: string;
  vendorId: string;
  title: string | null;
  customerName: string | null;
  /** "1315". */
  account: string | null;
  /** "CONTINENTAL EXPEDITED SERVICES - GY NA #1315", as printed. */
  accountText: string | null;
  commonOwner: string | null;
  nonsig: string | null;
  /** Fleet days, `YYYY-MM-DD`. */
  effectiveFrom: string;
  effectiveTo: string | null;
  notAFirmOffer: boolean;
  disclaimer: string | null;
  fileName: string;
  fileByteSize: number;
  fileContentHash: string;
  rowCounts: Record<PriceBookSheetKey, number>;
  lineCount: number;
  skippedRowCount: number;
  accountCheck: PriceBookAccountCheck;
  /** The account check in words, as stored. */
  accountCheckNote: string | null;
  /** The vendor's account reference when the book was loaded. */
  vendorAccountReference: string | null;
  /** The book this one replaced (a corrected book for the same date). */
  replacesPriceBookId: string | null;
  /** The book that replaced this one, when one did. */
  replacedByPriceBookId: string | null;
  loadedAt: string;
  loadedBy: PriceBookPerson | null;
  status: "live" | "withdrawn";
  withdrawnAt: string | null;
  withdrawnBy: PriceBookPerson | null;
  withdrawnReason: string | null;
}

/** `GET /vendors/:id/price-books`: every edition, newest effective date first. */
export interface PriceBookList {
  vendorId: string;
  vendorName: string;
  accountReference: string | null;
  books: PriceBookSummary[];
  /** The live book in force on the fleet's today, when there is one. */
  inForceTodayId: string | null;
  today: string;
}

/**
 * What an upload or a Replace did.
 *   loaded          a new book, written;
 *   replaced        a corrected book written and the old one withdrawn, in one step;
 *   already_loaded  the same file is already a live book of this vendor: nothing written.
 */
export interface PriceBookLoadResult {
  outcome: "loaded" | "replaced" | "already_loaded";
  book: PriceBookSummary;
  /** Said when the book loaded without its account being checked. */
  warning: string | null;
}

/** One price line as the book printed it. */
export interface PriceBookLineRow {
  lineId: string;
  sheet: PriceBookSheetKey;
  section: string | null;
  sourceRow: number;
  brand: string | null;
  productLine: string | null;
  description: string | null;
  size: string | null;
  loadRange: string | null;
  productCode: string | null;
  materialNumber: string | null;
  fullProductCode: string | null;
  category: string | null;
  quantity: string | null;
  basePrice: string | null;
  invoicePrice: string | null;
  fet: string | null;
  parts: string | null;
  labour: string | null;
  total: string | null;
  priceMarker: string | null;
  noFixedPrice: boolean;
  dealNumber: string | null;
}

/** `GET /vendors/:id/price-books/:bookId/lines`. */
export interface PriceBookLines {
  priceBookId: string;
  q: string | null;
  sheet: PriceBookSheetKey | null;
  items: PriceBookLineRow[];
  totalRows: number;
  limit: number;
  offset: number;
}

/* -------------------------------------------------------------------------- */
/* Paid versus book                                                            */
/* -------------------------------------------------------------------------- */

/** How a paid line was matched to a book line (apps/api/src/price-book/match.ts). */
export const PRICE_MATCH_BASES = [
  "product_code",
  "material_number",
  "service_code",
  "size_and_product_line",
  "size_and_description",
] as const;
export type PriceMatchBasis = (typeof PRICE_MATCH_BASES)[number];
export const PRICE_MATCH_BASIS_LABEL: Readonly<Record<PriceMatchBasis, string>> = {
  product_code: "Matched by product code",
  material_number: "Matched by material number",
  service_code: "Matched by service code",
  size_and_product_line: "Matched by size and product line",
  size_and_description: "Matched by size and description",
};

/** Why a paid line matched nothing in the book in force. */
export const PRICE_UNMATCHED_REASONS = [
  "no_code_or_size",
  "code_not_in_book",
  "code_shared",
  "no_line_for_size",
  "several_load_ranges",
  "several_lines",
  "book_size_disagrees",
] as const;
export type PriceUnmatchedReason = (typeof PRICE_UNMATCHED_REASONS)[number];

/**
 * Where a paid line stands against the book.
 *   compared        matched, and the book has a price: the difference is shown;
 *   no_fixed_price  matched, but the book prices it 'F/R', 'LMP' and the like;
 *   unmatched       a book was in force, and no single line of it fits;
 *   no_books        this vendor has no live price book;
 *   none_on_day     a book exists, but none was in force on the cost day;
 *   no_day          the line has no known cost day;
 *   ambiguous_book  two live books share the latest date (the database forbids it).
 */
export const PRICE_COMPARISON_STATUSES = [
  "compared",
  "no_fixed_price",
  "unmatched",
  "no_books",
  "none_on_day",
  "no_day",
  "ambiguous_book",
] as const;
export type PriceComparisonStatus = (typeof PRICE_COMPARISON_STATUSES)[number];

/**
 * How the paid price for one was read:
 *   unit_price                  the line's own unit price;
 *   amount_over_quantity        its amount divided by its quantity, to the cent;
 *   amount_quantity_not_stated  its amount, with no quantity on the line.
 */
export type PaidUnitBasis = "unit_price" | "amount_over_quantity" | "amount_quantity_not_stated";

export interface PriceBookInForce {
  priceBookId: string;
  effectiveFrom: string;
  notAFirmOffer: boolean;
}

export interface PriceBookMatch {
  basis: PriceMatchBasis;
  line: PriceBookLineRow;
  /** The book's invoice price (a service code's total), WITHOUT FET. Null when it has none. */
  bookPrice: string | null;
  fet: string | null;
  /** Paid minus the book price. Null when either is missing or the book has no fixed price. */
  difference: string | null;
  priceMarker: string | null;
}

export interface PriceBookComparisonRow {
  rowId: string;
  costEntryId: string;
  costDate: string | null;
  /** Where the cost day came from (`cost_date_source`). */
  costDateSource: string | null;
  workOrderId: string;
  workOrderLabel: string;
  description: string | null;
  /** The product code printed on the bill line, as read. */
  printedCode: string | null;
  quantity: string | null;
  unitOfMeasure: string | null;
  amount: string;
  paidUnitPrice: string;
  paidUnitBasis: PaidUnitBasis;
  invoiceNumber: string | null;
  /** FET charged on the same bill, shown beside and never compared. */
  fetOnBill: { description: string; amount: string }[];
  status: PriceComparisonStatus;
  /** The reason in words, for every status but `compared` and `no_fixed_price`. */
  sentence: string | null;
  unmatchedReason: PriceUnmatchedReason | null;
  /** Book lines that fitted equally well, when unmatched. */
  candidateCount: number;
  book: PriceBookInForce | null;
  match: PriceBookMatch | null;
}

/** `GET /vendors/:id/price-book-comparison`. */
export interface PriceBookComparison {
  vendorId: string;
  vendorName: string;
  /** Live books of this vendor. Zero means "No price book loaded for this vendor yet." */
  liveBooks: number;
  items: PriceBookComparisonRow[];
  totalRows: number;
  limit: number;
  offset: number;
}

/* -------------------------------------------------------------------------- */
/* Words                                                                       */
/* -------------------------------------------------------------------------- */

export const PRICE_BOOK_EMPTY = "No price book loaded for this vendor yet.";
export const PRICE_BOOK_NONE_ON_DAY = "No book was in force on this day.";
export const PRICE_BOOK_HISTORY_STARTS = "Item and price history starts with bills logged in TruckBuddy.";
export const PRICE_BOOK_NO_FIXED_PRICE = "No fixed price in the book";
export const PRICE_BOOK_NO_DAY = "This line has no known cost day, so no book can be chosen for it.";
export const PRICE_BOOK_AMBIGUOUS = "Two live books take effect on the same day, so neither is used.";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-03-10" → "10 Mar 2026". Anything that is not a day is returned as given. */
export function formatBookDay(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return day;
  const month = MONTHS[Number(match[2]) - 1];
  return month ? `${Number(match[3])} ${month} ${match[1]}` : day;
}

function nonZero(amount: string | null): amount is string {
  return amount !== null && Number(amount) !== 0;
}

/**
 * The line the panel prints for a paid line against the book:
 * "Paid $700.00 each - Book $692.26 + FET $34.59 - effective 10 Mar 2026, not a firm offer".
 * FET is left out when the book prints none or 0.00. A line the book prices
 * 'F/R' or 'LMP' reads "No fixed price in the book (F/R)". Every other status
 * is its own sentence.
 */
export function paidVersusBookText(row: PriceBookComparisonRow): string {
  const paid = `Paid ${formatMoney(row.paidUnitPrice)}${row.paidUnitBasis === "amount_quantity_not_stated" ? " (quantity not stated)" : " each"}`;
  const effective = row.book
    ? ` - effective ${formatBookDay(row.book.effectiveFrom)}${row.book.notAFirmOffer ? ", not a firm offer" : ""}`
    : "";
  if (row.status === "compared" && row.match?.bookPrice) {
    const fet = nonZero(row.match.fet) ? ` + FET ${formatMoney(row.match.fet)}` : "";
    return `${paid} - Book ${formatMoney(row.match.bookPrice)}${fet}${effective}`;
  }
  if (row.status === "no_fixed_price" || (row.status === "compared" && row.match && !row.match.bookPrice)) {
    const marker = row.match?.priceMarker ? ` (${row.match.priceMarker})` : "";
    return `${paid} - ${PRICE_BOOK_NO_FIXED_PRICE}${marker}${effective}`;
  }
  return `${paid} - ${row.sentence ?? PRICE_BOOK_NONE_ON_DAY}`;
}

/** "$7.74 over the book", "$3.00 under the book", "Same as the book"; null when there is no difference to state. */
export function differenceText(difference: string | null): string | null {
  if (difference === null) return null;
  const value = Number(difference);
  if (!Number.isFinite(value)) return null;
  if (value === 0) return "Same as the book";
  const abs = difference.startsWith("-") ? difference.slice(1) : difference;
  return `${formatMoney(abs)} ${value > 0 ? "over" : "under"} the book`;
}
